import ModelViewer from '@/components/features/models/Models'
import NotFound from '@/components/utils/NotFound'
import RouteLoadFailed from '@/components/utils/RouteLoadFailed'
import { throwLoaderError } from '@/lib/loader-errors'
import { useModelTheme } from '@/lib/modelTheme'
import {
  createTextureMap,
  filterManifestModelFiles,
  findObjFile,
  getMtlUrls,
} from '@/lib/modelUtils'
import { createFileRoute } from '@tanstack/react-router'
import {
  ErrorBoundary,
  type FallbackProps,
  getErrorMessage,
} from 'react-error-boundary'

import {
  AssetWithPresignedUrl,
  Manifest,
  PresignedUrlType,
} from '@droidmap/asset-service-contract'

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId_/_pathless/asset-sets/$assetSetId_/model',
)({
  loader: async ({ params, context }) => {
    const { assetSetId } = params
    const { assetClient } = context.client
    const { metadata } = context
    try {
      const assetSet = await assetClient.getAssetSet({ assetSetId }, metadata)
      const manifest = (await assetClient.getAssetSetManifest(
        { assetSetId, presignedUrlType: PresignedUrlType.GET },
        metadata,
      )) as AssetWithPresignedUrl
      let manifestData: Manifest | undefined
      try {
        const fetchedManifest = await fetch(manifest.presignedUrl)
        if (!fetchedManifest.ok) {
          console.error(
            `Failed to fetch manifest: ${fetchedManifest.status} ${fetchedManifest.statusText}`,
          )
        }
        manifestData = (await fetchedManifest.json()) as Manifest
      } catch (error) {
        console.error('Error fetching or parsing manifest:', error)
      }

      if (!manifestData) {
        throw new Error('Failed to load manifest data.')
      }

      const modelFiles = filterManifestModelFiles(manifestData)
      const modelAssets: AssetWithPresignedUrl[] = (await assetClient.getAssets(
        {
          assetIds: modelFiles.map((file) => file.assetId),
          presignedUrlType: PresignedUrlType.GET,
        },
        metadata,
      )) as AssetWithPresignedUrl[]
      const textureMap = createTextureMap(modelAssets)
      const objFile = findObjFile(modelAssets)
      const mtlUrls = getMtlUrls(modelAssets)
      return {
        crumb: 'model',
        assetSetId,
        assetSet,
        manifest,
        manifestData,
        metadata,
        modelAssets,
        textureMap,
        objFile,
        mtlUrls,
        layout: {
          variant: 'fullWidth',
        },
      }
    } catch (error) {
      // A 404/403 keeps the not-found screen (the asset set or its manifest is
      // genuinely absent); a 5xx or a blip on one of the several calls above
      // now gets a retry instead of a false "doesn't exist" (#2658).
      throwLoaderError(error, 'asset set model')
    }
  },
  component: Models,
  notFoundComponent: () => (
    <NotFound
      resource="Asset Set Model"
      message="The 3D model for this asset set doesn't exist or couldn't be loaded."
    />
  ),
  errorComponent: () => <RouteLoadFailed resource="3D model" />,
})

// react-error-boundary 6 widened FallbackProps: `error` is now `unknown` (it was
// `Error`) and `resetErrorBoundary` is variadic. Typing against FallbackProps
// directly keeps this in step with the library, and getErrorMessage is v6's own
// helper for turning an unknown thrown value into a displayable string.
const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => (
  <div className="bg-panel border-danger/40 rounded-md border p-4">
    <h2 className="text-danger text-xl font-bold">Something went wrong:</h2>
    <pre className="text-text-dim mt-2 text-sm">
      {getErrorMessage(error) ?? 'Unknown error'}
    </pre>
    <button
      onClick={resetErrorBoundary}
      className="bg-danger text-btn-fg hover:bg-danger/90 mt-4 rounded-md px-4 py-2 transition-colors"
    >
      Try again
    </button>
  </div>
)

function Models() {
  const { assetSetId, textureMap, objFile, mtlUrls } = Route.useLoaderData()
  const modelTheme = useModelTheme()

  if (!assetSetId) {
    throw new Error('No asset set ID provided')
  }

  if (!objFile) {
    return <div className="p-4">Model file not found</div>
  }

  // Event handlers
  const handleError = (error: Error) => {
    console.error('Failed to load model:', error)
  }

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <ModelViewer
          objUrl={objFile.presignedUrl}
          mtlUrls={mtlUrls}
          textureUrlMap={textureMap}
          backgroundColor={modelTheme.background}
          onError={handleError}
        />
      </ErrorBoundary>
    </>
  )
}
